import {
  type EstimateExtraction,
  type EstimateItem,
  PRODUCTS,
  type ProposalDetail,
  type ProposalFormState,
  type ProposalListItem,
  TRADE_ORDER,
  type UploadSlots,
  buildProposal,
  buildRequestFromForm,
  computePlan,
  displayedOutOfPocketCents,
  formLineAmounts,
  groupKey,
  isOwnerItem,
  lineLabel,
  setAllIncluded,
  setGroupIncluded,
  setLineIncluded,
  showsRecoverableDepreciation,
  warningsBlockSending,
} from "@nmt/shared";
import { AfterSend, HowToSend, type SendHow } from "../components/email-dialog";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { del, errorMessage, get, patch, post } from "../api";
import { Alert, Badge, Button, Dialog, Empty, Field, Loading, Panel, PanelHeader, SelectField, TextAreaField, cx, formatCents, formatDateTime, parseDollars } from "../components/ui";
import { useSession } from "../session";
import { NewRetailDialog, RetailEditor } from "./RetailProposal";
import { CustomEditor } from "./CustomProposal";

const STATUS: Record<ProposalListItem["status"], { label: string; tone: "ok" | "wait" | "off" | "brand" }> = {
  reading: { label: "Reading estimate", tone: "wait" },
  read_failed: { label: "Couldn't read", tone: "off" },
  draft: { label: "Draft", tone: "wait" },
  sent: { label: "Sent", tone: "brand" },
  viewed: { label: "Opened", tone: "brand" },
  signed: { label: "Signed", tone: "ok" },
  declined: { label: "Declined", tone: "off" },
  void: { label: "Void", tone: "off" },
};

export function ProposalStatus({ status }: { status: ProposalListItem["status"] }) {
  return <Badge tone={STATUS[status].tone}>{STATUS[status].label}</Badge>;
}

/** The job's Proposals tab: list, and a new proposal from the carrier's estimate PDF. */
export function JobProposals({ jobId }: { jobId: string }) {
  const { can } = useSession();
  const navigate = useNavigate();
  const list = useQuery({ queryKey: ["proposals", jobId], queryFn: () => get<ProposalListItem[]>(`/api/jobs/${jobId}/proposals`) });
  const qcList = useQueryClient();
  const canDelete = useSession().can("proposals.edit");
  const remove = useMutation({ mutationFn: (id: string) => del(`/api/proposals/${id}`), onSuccess: () => qcList.invalidateQueries({ queryKey: ["proposals", jobId] }) });
  const input = useRef<HTMLInputElement>(null);
  const start = useMutation({
    mutationFn: async (f: File) => {
      const [slot] = await post<UploadSlots[]>(`/api/jobs/${jobId}/files/upload-urls`, { files: [{ name: f.name, contentType: "application/pdf", size: f.size }] });
      const res = await fetch(slot!.original.url, { method: "PUT", headers: slot!.original.headers, body: f });
      if (!res.ok) throw new Error("Upload failed. Try again.");
      const saved = await post<{ id: string }>(`/api/jobs/${jobId}/files`, { key: slot!.original.key, name: f.name, contentType: "application/pdf", size: f.size, category: "Insurance" });
      return post<ProposalDetail>(`/api/jobs/${jobId}/proposals`, { estimateFileId: saved.id });
    },
    onSuccess: (p) => navigate(`/proposals/${p.id}`),
  });
  const [picking, setPicking] = useState(false);
  const startRetail = useMutation({
    mutationFn: (variant: string) => post<ProposalDetail>(`/api/jobs/${jobId}/proposals`, variant === "custom" ? { kind: "custom" } : { kind: "retail", variant }),
    onSuccess: (p) => navigate(`/proposals/${p.id}`),
  });
  return (
    <Panel>
      <PanelHeader
        title="Proposals"
        description="Insurance: upload the carrier's estimate (the Xactimate PDF); it's read in a minute or two. Retail roof: priced from the price list. Custom: remodels, repairs, commercial, anything else."
        action={
          can("proposals.edit") && (
            <>
              <input
                ref={input}
                type="file"
                accept="application/pdf,.pdf"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) start.mutate(f);
                  e.target.value = "";
                }}
              />
              <div className="flex flex-wrap gap-2">
                <Button variant="secondary" className="px-3 py-1.5" onClick={() => setPicking(true)}>
                  New retail or custom proposal
                </Button>
                <Button variant="primary" className="px-3 py-1.5" busy={start.isPending} onClick={() => input.current?.click()}>
                  New insurance proposal
                </Button>
              </div>
              {picking && <NewRetailDialog busy={startRetail.isPending} error={startRetail.error} onPick={(v) => startRetail.mutate(v)} onClose={() => setPicking(false)} />}
            </>
          )
        }
      />
      {start.error && (
        <div className="px-5 pt-4">
          <Alert>{errorMessage(start.error)}</Alert>
        </div>
      )}
      {list.isLoading ? (
        <div className="px-5">
          <Loading />
        </div>
      ) : !list.data?.length ? (
        <Empty>No proposals yet.</Empty>
      ) : (
        <ul>
          {list.data.map((p) => (
            <li key={p.id} className="flex items-center border-b border-mist last:border-0">
              <Link to={`/proposals/${p.id}`} className="flex min-w-0 flex-1 flex-wrap items-center gap-3 px-5 py-3 hover:bg-mist/50">
                <ProposalStatus status={p.status} />
                <span className="font-semibold">{p.title}</span>
                {p.contractCents !== null && <span>{formatCents(p.contractCents)}</span>}
                <span className="text-sm text-muted">
                  {[p.signerName, p.signedAt ? `signed ${formatDateTime(p.signedAt)}` : p.sentAt ? `sent ${formatDateTime(p.sentAt)}` : `started ${formatDateTime(p.createdAt)}`, p.createdBy?.name].filter(Boolean).join(" · ")}
                </span>
              </Link>
              {canDelete && p.status !== "signed" && (
                <button
                  className="px-4 py-3 text-sm text-muted hover:text-danger"
                  aria-label="Delete this proposal"
                  disabled={remove.isPending}
                  onClick={() =>
                    window.confirm(p.status === "sent" || p.status === "viewed" ? "Delete this proposal? The customer's link stops working. It goes to the recycle bin." : "Delete this proposal? It goes to the recycle bin.") && remove.mutate(p.id)
                  }
                >
                  Delete
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

/** One proposal: reading, the rep's choices with live totals, sending, and what happened after. */
export function ProposalPage() {
  const { id = "" } = useParams();
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["proposal", id],
    queryFn: () => get<ProposalDetail>(`/api/proposals/${id}`),
    refetchInterval: (query) => (query.state.data?.status === "reading" ? 3000 : false),
  });
  if (q.isLoading) return <Loading />;
  if (q.error) return <Alert>{errorMessage(q.error)}</Alert>;
  const p = q.data!;
  const set = (d: ProposalDetail) => qc.setQueryData(["proposal", id], d);

  return (
    <div className="flex flex-col gap-5">
      <div>
        <div className="text-sm text-muted">
          <Link to={`/jobs/${p.job.id}`} className="hover:text-ink">
            Job #{p.job.number} {p.customer.name}
          </Link>{" "}
          · {p.job.workflowName}
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-3">
          <h1 className="page-title">{p.title}</h1>
          <ProposalStatus status={p.status} />
        </div>
      </div>
      {p.status === "reading" ? (
        <Panel className="flex items-center gap-4 px-5 py-6">
          <span className="size-5 animate-spin rounded-full border-2 border-brand border-r-transparent" aria-hidden />
          <div>
            <div className="font-semibold">Reading {p.estimateFile?.name ?? "the estimate"}…</div>
            <div className="text-sm text-muted">This takes a minute or two. Every line is checked against the estimate's own totals. You can leave this page; it keeps going.</div>
          </div>
        </Panel>
      ) : p.status === "read_failed" ? (
        <ReadFailed p={p} onChange={set} />
      ) : p.status === "draft" ? (
        p.kind === "retail" ? <RetailEditor p={p} onChange={set} /> : p.kind === "custom" ? <CustomEditor p={p} onChange={set} /> : <Editor p={p} onChange={set} />
      ) : (
        <Sent p={p} onChange={set} />
      )}
    </div>
  );
}

function Checks({ checks }: { checks: NonNullable<ProposalDetail["checks"]> }) {
  return (
    <ul className="flex flex-col gap-1 text-sm">
      {checks.map((c) => (
        <li key={c.name} className="flex gap-2">
          <span className={cx("font-bold", c.ok ? "text-ok" : "text-danger")}>{c.ok ? "✓" : "✗"}</span>
          <span>
            <span className="font-medium">{c.name}</span> <span className="text-muted">{c.detail}</span>
          </span>
        </li>
      ))}
    </ul>
  );
}

function ReadFailed({ p, onChange }: { p: ProposalDetail; onChange: (d: ProposalDetail) => void }) {
  const { can } = useSession();
  const again = useMutation({ mutationFn: () => post<ProposalDetail>(`/api/proposals/${p.id}/read-again`), onSuccess: onChange });
  return (
    <Panel className="flex flex-col gap-4 px-5 py-5">
      <Alert>{p.readError ?? "The estimate couldn't be read."}</Alert>
      {p.checks && <Checks checks={p.checks} />}
      <div className="flex flex-wrap gap-2">
        {p.estimateFile && (
          <a href={p.estimateFile.url} target="_blank" rel="noreferrer" className="inline-flex items-center rounded-[var(--radius-control)] px-4 py-2 text-sm font-semibold hover:bg-mist">
            Open the estimate
          </a>
        )}
        {can("proposals.edit") && (
          <Button onClick={() => again.mutate()} busy={again.isPending}>
            Read it again
          </Button>
        )}
      </div>
    </Panel>
  );
}

/** Groups of estimate lines in the Worker's order: by trade, then building. */
function groupsOf(ex: EstimateExtraction) {
  const map = new Map<string, EstimateItem[]>();
  for (const i of ex.items) map.set(groupKey(i), [...(map.get(groupKey(i)) ?? []), i]);
  const rank = (key: string) => {
    const t = TRADE_ORDER.indexOf(key.split(" - ")[0]!);
    return t === -1 ? TRADE_ORDER.length : t;
  };
  return [...map.entries()].sort((a, b) => rank(a[0]) - rank(b[0]) || a[0].localeCompare(b[0]));
}

function Editor({ p, onChange }: { p: ProposalDetail; onChange: (d: ProposalDetail) => void }) {
  const ex = p.extraction as EstimateExtraction;
  const [form, setForm] = useState(p.form as ProposalFormState);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState<{ d: ProposalDetail; how: SendHow } | null>(null);
  const customer = useMemo(() => ({ ...p.customer }), [p.customer]);

  // Live totals, computed here with the same code the server uses.
  const built = useMemo(() => {
    try {
      const r = buildProposal(ex, buildRequestFromForm(form, customer), p.rep);
      return { ...r, plan: computePlan(r.input), error: null as string | null };
    } catch (e) {
      return { input: null, warnings: [], plan: null, error: errorMessage(e) };
    }
  }, [ex, form, customer, p.rep]);

  // Save as they go (half a second after the last change).
  const save = useMutation({ mutationFn: (f: ProposalFormState) => patch<ProposalDetail>(`/api/proposals/${p.id}`, { form: f }) });
  const first = useRef(true);
  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    const t = window.setTimeout(() => save.mutate(form), 500);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  const included = new Set(form.included);
  const groups = groupsOf(ex);

  return (
    <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_340px]">
      <div className="flex flex-col gap-5">
        {p.checks && (
          <details className="rounded-[var(--radius-panel)] bg-white px-5 py-3 shadow-[var(--shadow-card)]">
            <summary className="cursor-pointer text-sm font-semibold">
              Estimate read: {ex.items.length} lines, all totals check out{p.readPasses === 2 ? " (on the second pass)" : ""}.{" "}
              {p.estimateFile && (
                <a href={p.estimateFile.url} target="_blank" rel="noreferrer" className="font-medium text-brand hover:underline">
                  Open the estimate
                </a>
              )}
            </summary>
            <div className="mt-3">
              <Checks checks={p.checks} />
            </div>
          </details>
        )}

        <Panel>
          <PanelHeader
            title="The work we're doing"
            description="Checked lines are in our contract. Unchecked lines stay on the proposal under Not included."
            action={
              <div className="flex gap-1">
                <Button className="px-3 py-1.5" onClick={() => setForm(setAllIncluded(form, ex, true))}>
                  Include all
                </Button>
                <Button className="px-3 py-1.5" onClick={() => setForm(setAllIncluded(form, ex, false))}>
                  Exclude all
                </Button>
              </div>
            }
          />
          <div className="flex flex-col divide-y divide-mist">
            {groups.map(([key, items]) => {
              const owner = items.every((i) => isOwnerItem(i, ex));
              const on = items.filter((i) => included.has(i.n));
              const g = form.groups[key] ?? {};
              return (
                <section key={key} className={cx("px-5 py-4", owner && "opacity-60")}>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <label className="flex items-center gap-2 font-semibold">
                      {!owner && (
                        <input
                          type="checkbox"
                          checked={on.length === items.length}
                          ref={(el) => {
                            if (el) el.indeterminate = on.length > 0 && on.length < items.length;
                          }}
                          onChange={(e) => setForm(setGroupIncluded(form, ex, key, e.target.checked))}
                          className="size-4 accent-[var(--color-brand)]"
                        />
                      )}
                      {key}
                    </label>
                    <span className="text-sm text-muted">{owner ? "Paid to the homeowner separately" : formatCents(on.reduce((s, i) => s + i.rcvCents, 0))}</span>
                  </div>
                  {!owner && on.length > 0 && (
                    <div className="mt-3 grid gap-3 sm:grid-cols-2">
                      <Field label="Heading on proposal" value={g.heading ?? ""} placeholder={key} onChange={(e) => setForm({ ...form, groups: { ...form.groups, [key]: { ...g, heading: e.target.value } } })} />
                      <Field
                        label="Summary on proposal"
                        value={g.summary ?? ex.groupSummaries?.[key] ?? ""}
                        onChange={(e) => setForm({ ...form, groups: { ...form.groups, [key]: { ...g, summary: e.target.value } } })}
                      />
                    </div>
                  )}
                  <div className="mt-3 overflow-x-auto">
                    <table className="table-base text-sm">
                      <thead>
                        <tr>
                          <th className="w-8" />
                          <th>#</th>
                          <th>Line</th>
                          <th className="text-right">RCV</th>
                          <th className="text-right">Depreciation</th>
                          <th className="text-right">ACV</th>
                        </tr>
                      </thead>
                      <tbody>
                        {items.map((i) => {
                          const a = formLineAmounts(i);
                          const isOwner = isOwnerItem(i, ex);
                          return (
                            <tr key={i.n} className={cx(!included.has(i.n) && "text-muted")}>
                              <td>
                                {!isOwner && (
                                  <input
                                    type="checkbox"
                                    checked={included.has(i.n)}
                                    onChange={(e) => setForm(setLineIncluded(form, i.n, e.target.checked))}
                                    aria-label={`Include line ${i.n}`}
                                    className="size-4 accent-[var(--color-brand)]"
                                  />
                                )}
                              </td>
                              <td>{i.n}</td>
                              <td>
                                {lineLabel(i)}
                                {i.rpsFactor && i.rpsFactor !== "N/A" && <span className="ml-1 rounded bg-danger-wash px-1 text-xs font-semibold text-danger">RPS {i.rpsFactor}</span>}
                              </td>
                              <td className="text-right whitespace-nowrap">{formatCents(i.rcvCents)}</td>
                              <td className="text-right whitespace-nowrap">{a.recoverable ? formatCents(a.depreciationCents) : `<${formatCents(a.depreciationCents)}>`}</td>
                              <td className="text-right whitespace-nowrap">{formatCents(a.acvCents)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </section>
              );
            })}
          </div>
        </Panel>

        <DealDetails form={form} setForm={setForm} />
        <Chat p={p} onChange={(d) => (setForm(d.form as ProposalFormState), onChange(d))} />
      </div>

      <aside className="flex flex-col gap-4 xl:sticky xl:top-4 xl:self-start">
        <Summary built={built} />
        <Panel className="flex flex-col gap-2 px-5 py-4">
          <a href={`/api/proposals/${p.id}/preview.pdf`} target="_blank" rel="noreferrer" className="inline-flex justify-center rounded-[var(--radius-control)] bg-mist px-4 py-2 text-sm font-semibold hover:bg-line">
            Preview PDF
          </a>
          <Button variant="primary" disabled={!built.input || warningsBlockSending(built.warnings)} onClick={() => setSending(true)}>
            Send to customer…
          </Button>
          <p className="text-xs text-muted">{save.isPending ? "Saving…" : save.error ? errorMessage(save.error) : "Saved."}</p>
        </Panel>
      </aside>
      {sending && (
        <SendDialog
          p={p}
          form={form}
          total={built.plan?.grandTotalCents ?? 0}
          onClose={() => setSending(false)}
          onSent={(d, how) => {
            setSending(false);
            setSent({ d, how });
          }}
        />
      )}
      {sent && <LinkDialog p={sent.d} how={sent.how} onClose={() => onChange(sent.d)} />}
    </div>
  );
}

type Built = { warnings: { code: string; message: string }[]; plan: ReturnType<typeof computePlan> | null; error: string | null };

/** The rep's sidebar, as the Worker showed it. */
function Summary({ built }: { built: Built }) {
  const plan = built.plan;
  if (!plan) return <Alert>{built.error ?? "Pick some lines."}</Alert>;
  const out = displayedOutOfPocketCents(plan);
  const row = (label: string, cents: number, opts: { strong?: boolean; minus?: boolean } = {}) => (
    <div className={cx("flex justify-between gap-3 py-1", opts.strong && "font-semibold")}>
      <span>{label}</span>
      <span>
        {opts.minus ? "–" : ""}
        {formatCents(cents)}
      </span>
    </div>
  );
  return (
    <Panel className="px-5 py-4 text-sm">
      <h2 className="mb-2 font-semibold">Summary</h2>
      {row("Insurance approved (RCV)", plan.claimRcvCents)}
      {row("Our insurance work", plan.contractRcvCents)}
      {row("Deductible", plan.deductibleDueCents)}
      {row("ACV for our work", plan.acvCheckDueCents)}
      {showsRecoverableDepreciation(plan) && row("Recoverable depreciation", plan.contractRecDepCents)}
      {plan.contractPwiCents > 0 && row("Code upgrade", plan.contractPwiCents)}
      {plan.contractNonRecDepCents > 0 && row("Non-recoverable dep.", plan.contractNonRecDepCents)}
      {plan.upgradesTotalCents > 0 && row("Upgrades", plan.upgradesTotalCents)}
      <div className="my-1 border-t border-line" />
      {row("Contract total", plan.grandTotalCents, { strong: true })}
      <div className="mt-2 rounded-[var(--radius-control)] bg-ink px-3 py-2 text-white">
        <div className="text-xs font-semibold tracking-wide uppercase opacity-75">Estimated out of pocket</div>
        <div className="text-xl font-semibold">{formatCents(out)}</div>
        {plan.outOfPocketCents < 0 && <div className="text-xs opacity-75">Insurance funds exceed the contract by {formatCents(-plan.outOfPocketCents)}.</div>}
      </div>
      {built.warnings.map((w) => (
        <div key={w.code} className="mt-3">
          <Alert tone={w.code === "claim_mismatch" ? "danger" : "info"}>{w.message}</Alert>
        </div>
      ))}
    </Panel>
  );
}

function DealDetails({ form, setForm }: { form: ProposalFormState; setForm: (f: ProposalFormState) => void }) {
  const [ded, setDed] = useState(formatCents(form.deductibleCents).replace("$", ""));
  const [upTotal, setUpTotal] = useState(form.upgrades.totalCents ? formatCents(form.upgrades.totalCents).replace("$", "") : "");
  const list = (k: "items" | "noCharge") => (
    <TextAreaField
      label={k === "items" ? "Upgrades (one per line)" : "No-charge upgrades (one per line)"}
      rows={3}
      value={form.upgrades[k].join("\n")}
      onChange={(e) => setForm({ ...form, upgrades: { ...form.upgrades, [k]: e.target.value.split("\n") } })}
    />
  );
  return (
    <Panel>
      <PanelHeader title="Deal details" />
      <div className="grid gap-4 px-5 py-4 sm:grid-cols-2">
        <Field
          label="Deductible"
          inputMode="decimal"
          value={ded}
          onChange={(e) => {
            setDed(e.target.value);
            const c = parseDollars(e.target.value);
            if (c !== null && !Number.isNaN(c)) setForm({ ...form, deductibleCents: c });
          }}
        />
        <SelectField label="Roofing product" value={form.product.key} onChange={(e) => setForm({ ...form, product: { ...form.product, key: e.target.value as ProposalFormState["product"]["key"] } })}>
          <option value="flex">{PRODUCTS.flex.name}</option>
          <option value="duration">{PRODUCTS.duration.name}</option>
          <option value="other">Other…</option>
        </SelectField>
        {form.product.key === "other" && (
          <>
            <Field label="Product name" value={form.product.name ?? ""} onChange={(e) => setForm({ ...form, product: { ...form.product, name: e.target.value } })} />
            <Field label="Product description" value={form.product.description ?? ""} onChange={(e) => setForm({ ...form, product: { ...form.product, description: e.target.value } })} />
          </>
        )}
        {list("items")}
        {list("noCharge")}
        <Field
          label="Upgrade total"
          inputMode="decimal"
          value={upTotal}
          hint="What the customer pays for the upgrades, beyond insurance."
          onChange={(e) => {
            setUpTotal(e.target.value);
            const c = parseDollars(e.target.value);
            if (!Number.isNaN(c)) setForm({ ...form, upgrades: { ...form.upgrades, totalCents: c ?? 0 } });
          }}
        />
        <TextAreaField className="sm:col-span-2" label="Project notes (one per line)" rows={3} value={form.notes.join("\n")} onChange={(e) => setForm({ ...form, notes: e.target.value.split("\n") })} />
      </div>
    </Panel>
  );
}

/** "Ask for changes": plain-language edits to the form. */
function Chat({ p, onChange }: { p: ProposalDetail; onChange: (d: ProposalDetail) => void }) {
  const [text, setText] = useState("");
  const send = useMutation({
    mutationFn: () => post<ProposalDetail>(`/api/proposals/${p.id}/chat`, { message: text }),
    onSuccess: (d) => {
      setText("");
      onChange(d);
    },
  });
  if (!p.aiAvailable) return null;
  return (
    <Panel>
      <PanelHeader title="Ask for changes" description={'"Exclude the garage door", "upgrade total is 3850", "reword the roofing summary".'} />
      {!!p.chat.length && (
        <ul className="flex max-h-72 flex-col gap-2 overflow-y-auto px-5 py-3 text-sm">
          {p.chat.map((m, i) => (
            <li key={i} className={cx("max-w-[85%] rounded-[var(--radius-control)] px-3 py-2", m.role === "user" ? "self-end bg-ink text-white" : "self-start bg-mist")}>
              {m.text}
            </li>
          ))}
        </ul>
      )}
      <form
        className="flex gap-2 border-t border-mist px-5 py-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (text.trim()) send.mutate();
        }}
      >
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="What should change?"
          aria-label="Ask for changes"
          className="flex-1 rounded-[var(--radius-control)] border border-line px-3 py-2 focus:border-brand focus:ring-2 focus:ring-brand/20 focus:outline-none"
        />
        <Button type="submit" variant="primary" busy={send.isPending} disabled={!text.trim()}>
          Ask
        </Button>
      </form>
      {send.error && (
        <div className="px-5 pb-3">
          <Alert>{errorMessage(send.error)}</Alert>
        </div>
      )}
    </Panel>
  );
}

function SendDialog({ p, form, total, onClose, onSent }: { p: ProposalDetail; form: ProposalFormState; total: number; onClose: () => void; onSent: (d: ProposalDetail, how: SendHow) => void }) {
  const [how, setHow] = useState<SendHow>("email");
  // The customer as they are now (the form's copy is from when the proposal was started).
  const [name, setName] = useState(p.customer.name || form.signerName);
  const [email, setEmail] = useState(p.customer.email || form.signerEmail);
  const send = useMutation({ mutationFn: () => post<ProposalDetail>(`/api/proposals/${p.id}/send`, { signerName: name, signerEmail: email }), onSuccess: (d) => onSent(d, how) });
  return (
    <Dialog
      title="Send to customer"
      onClose={onClose}
      footer={
        <>
          <Button variant="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" busy={send.isPending} onClick={() => send.mutate()}>
            Make the proposal for {formatCents(total)}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {send.error && <Alert>{errorMessage(send.error)}</Alert>}
        <p className="text-sm text-ink-soft">This locks the proposal, saves the PDF to the job's files, and gives you a link to send the customer. They review and sign on their phone or computer; no account needed.</p>
        <Field label="Who's signing" value={name} onChange={(e) => setName(e.target.value)} required />
        <Field label="Their email (for the record)" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        <HowToSend value={how} onChange={setHow} />
      </div>
    </Dialog>
  );
}

/** The customer's link, shown once right after sending (it isn't stored). */
export function LinkDialog({ p, how = "link", onClose }: { p: ProposalDetail; how?: SendHow; onClose: () => void }) {
  return <AfterSend how={how} kind="proposal" id={p.id} link={p.customerUrl!} jobId={p.jobId} onClose={onClose} linkNote={`Send this link to ${p.signerName}. Copy it now: for security it can't be shown again (you can make a new one later).`} />;
}

function Sent({ p, onChange }: { p: ProposalDetail; onChange: (d: ProposalDetail) => void }) {
  const { can } = useSession();
  const [link, setLink] = useState<ProposalDetail | null>(null);
  const newLink = useMutation({ mutationFn: () => post<ProposalDetail>(`/api/proposals/${p.id}/new-link`), onSuccess: setLink });
  const voidIt = useMutation({ mutationFn: () => post<ProposalDetail>(`/api/proposals/${p.id}/void`), onSuccess: onChange });
  const navigate = useNavigate();
  const revise = useMutation({ mutationFn: () => post<ProposalDetail>(`/api/proposals/${p.id}/revise`), onSuccess: (d) => navigate(`/proposals/${d.id}`) });
  return (
    <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_340px]">
      <Panel className="overflow-hidden">
        {p.signedPdfUrl || p.pdfUrl ? <iframe src={p.signedPdfUrl ?? p.pdfUrl!} title="Proposal PDF" className="h-[75dvh] w-full" /> : <Empty>No PDF.</Empty>}
      </Panel>
      <aside className="flex flex-col gap-4">
        <Panel className="px-5 py-4 text-sm">
          <div className="flex justify-between py-1 font-semibold">
            <span>Contract total</span>
            <span>{formatCents(p.contractCents ?? 0)}</span>
          </div>
          {p.kind === "insurance" && (
            <div className="flex justify-between py-1">
              <span>Estimated out of pocket</span>
              <span>{formatCents(p.outOfPocketCents ?? 0)}</span>
            </div>
          )}
          <dl className="mt-3 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-ink-soft">
            <dt className="text-muted">Signer</dt>
            <dd>{p.signerName}</dd>
            {p.sentAt && (
              <>
                <dt className="text-muted">Sent</dt>
                <dd>{formatDateTime(p.sentAt)}</dd>
              </>
            )}
            {p.signedAt && (
              <>
                <dt className="text-muted">Signed</dt>
                <dd>{formatDateTime(p.signedAt)}</dd>
              </>
            )}
            {p.custom?.poNumber && (
              <>
                <dt className="text-muted">PO number</dt>
                <dd>{p.custom.poNumber}</dd>
              </>
            )}
            {p.custom?.signerTitle && (
              <>
                <dt className="text-muted">Signer</dt>
                <dd>{[p.custom.signerTitle, p.custom.signerCompany].filter(Boolean).join(", ")}</dd>
              </>
            )}
            {p.validThrough && !p.signedAt && (
              <>
                <dt className="text-muted">Valid through</dt>
                <dd>{new Date(`${p.validThrough}T12:00`).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</dd>
              </>
            )}
          </dl>
        </Panel>
        <Panel className="flex flex-col gap-2 px-5 py-4">
          {p.signedPdfUrl && (
            <a href={p.signedPdfUrl} target="_blank" rel="noreferrer" className="inline-flex justify-center rounded-[var(--radius-control)] bg-mist px-4 py-2 text-sm font-semibold hover:bg-line">
              Signed PDF
            </a>
          )}
          {p.pdfUrl && (
            <a href={p.pdfUrl} target="_blank" rel="noreferrer" className="inline-flex justify-center rounded-[var(--radius-control)] bg-mist px-4 py-2 text-sm font-semibold hover:bg-line">
              PDF as sent
            </a>
          )}
          {can("proposals.edit") && (p.status === "sent" || p.status === "viewed") && (
            <>
              <Button busy={newLink.isPending} onClick={() => newLink.mutate()}>
                New customer link
              </Button>
              <Button variant="quiet" busy={voidIt.isPending} onClick={() => window.confirm("Void this proposal? The customer's link stops working.") && voidIt.mutate()}>
                Void
              </Button>
            </>
          )}
          {can("proposals.edit") && p.kind === "custom" && ["sent", "viewed", "declined"].includes(p.status) && (
            <Button busy={revise.isPending} onClick={() => revise.mutate()}>
              {p.custom?.revisedBy ? "Open the revision" : `Revise (Rev ${(p.custom?.revision ?? 0) + 1})`}
            </Button>
          )}
          {p.custom?.revisionOf && (
            <Link to={`/proposals/${p.custom.revisionOf}`} className="text-center text-sm text-muted hover:text-ink">
              The version this revises
            </Link>
          )}
          {p.status === "void" && p.custom?.revisedBy && (
            <Link to={`/proposals/${p.custom.revisedBy}`} className="text-center text-sm font-semibold text-brand hover:underline">
              Replaced by a revision
            </Link>
          )}
          {(newLink.error || voidIt.error || revise.error) && <Alert>{errorMessage(newLink.error ?? voidIt.error ?? revise.error)}</Alert>}
        </Panel>
      </aside>
      {link && <LinkDialog p={link} onClose={() => (onChange(link), setLink(null))} />}
    </div>
  );
}
